/**
 * CreateDealButton — start a new deal pre-linked to a contact.
 *
 * Lives on the Contact detail page. Picks a pipeline (default preselected)
 * and drops the new deal into that pipeline's first stage with the contact
 * attached, then navigates to the new deal.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import { useFiasLeadsPipelines, useCreateFiasLeadsDeal } from '../hooks/useFiasLeads';

export function CreateDealButton({
  holderId,
  contactId,
  contactName,
}: {
  holderId: string;
  contactId: string;
  contactName: string;
}) {
  const navigate = useNavigate();
  const pipelinesQuery = useFiasLeadsPipelines(holderId);
  const createDeal = useCreateFiasLeadsDeal(holderId);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [pipelineId, setPipelineId] = useState('');

  const pipelines = pipelinesQuery.data ?? [];
  const selectedPipeline =
    pipelines.find((p) => p.id === pipelineId) ??
    pipelines.find((p) => p.is_default) ??
    pipelines[0];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPipeline) {
      alert('Create a pipeline before adding deals.');
      return;
    }
    const firstStage = selectedPipeline.stages[0];
    if (!firstStage) {
      alert('That pipeline has no stages yet.');
      return;
    }
    try {
      const res = await createDeal.mutateAsync({
        pipelineId: selectedPipeline.id,
        stageId: firstStage.id,
        name: name.trim() || `${contactName} — new deal`,
        contactId,
      });
      setOpen(false);
      setName('');
      navigate(`${ARCHE_PATH}/deals/${res.deal.id}`);
    } catch (err) {
      alert(`Could not create deal: ${(err as Error).message}`);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 text-sm rounded border border-border px-3 py-1.5 hover:bg-muted/30"
      >
        <Plus className="h-4 w-4" />
        Create deal
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-wrap items-center gap-2 rounded border border-border bg-card p-2"
    >
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={`${contactName} — new deal`}
        aria-label="Deal name"
        className="min-w-[180px] flex-1 rounded border border-border bg-background px-3 py-1.5 text-sm"
        autoFocus
      />
      <select
        value={selectedPipeline?.id ?? ''}
        onChange={(e) => setPipelineId(e.target.value)}
        aria-label="Pipeline"
        className="rounded border border-border bg-background px-2 py-1.5 text-sm"
      >
        {pipelines.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
            {p.is_default ? ' (default)' : ''}
          </option>
        ))}
      </select>
      <button
        type="submit"
        disabled={createDeal.isPending || pipelines.length === 0}
        className="rounded bg-primary px-3 py-1.5 text-primary-foreground text-sm disabled:opacity-50"
      >
        {createDeal.isPending ? 'Creating…' : 'Create'}
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="rounded border border-border px-3 py-1.5 text-sm"
      >
        Cancel
      </button>
    </form>
  );
}
